/* Home motion: (1) tap-to-shake showcase, (2) scroll-driven "how it works" clip with doodles. */
(function () {
  const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const near = (el, fn) => {
    if (!('IntersectionObserver' in window)) { fn(); return; }
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { io.disconnect(); fn(); } }, { rootMargin: '400px 0px' });
    io.observe(el);
  };

  /* ---------- 1 · shake ----------
     The binder is a damped spring in rotation; a tap is a real shake (a push one way, half a swing later a push back).
     Each loose piece feels the tilt and the swing in the binder's own frame, knocks the window edge and its neighbours,
     and once the binder calms down it is drawn back to exactly where it sits in the photo. Fixed 240 Hz physics. */
  (function () {
    const wrap = document.querySelector('[data-dv-shake-wrap]');
    if (!wrap) return;
    const box = wrap.querySelector('[data-dv-shake]');
    const still = box.querySelector('.dv-shake__still');
    let GEO, URLS;
    try { GEO = JSON.parse(wrap.querySelector('[data-dv-shake-geo]').textContent); URLS = JSON.parse(wrap.querySelector('[data-dv-shake-assets]').textContent); } catch (e) { return; }
    const G = 9810, PIVOT = [600, 900], DT = 1 / 240, HOP_G = 4200;
    const BOOK = { w: 2 * Math.PI * 2.3, z: 0.1, push: 0.085, pushT: 0.07, back: 0.217 };
    const loadImg = (src) => new Promise((ok, no) => { const i = new Image(); i.decoding = 'async'; i.onload = () => (i.decode ? i.decode().then(() => ok(i), () => ok(i)) : ok(i)); i.onerror = no; i.src = src; });
    const smooth = (u) => { u = Math.max(0, Math.min(1, u)); return u * u * (3 - 2 * u); };

    function pip(poly, x, y) {
      let c = false;
      for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
        const [xi, yi] = poly[i], [xj, yj] = poly[j];
        if ((yi > y) !== (yj > y) && x < (xj - xi) * (y - yi) / (yj - yi) + xi) c = !c;
      }
      return c;
    }
    function wallDist(poly, x, y) {          // nearest point on the window outline: signed distance and the inward direction
      let best = { d: 1e9, dx: 0, dy: 0 };
      for (let i = 0; i < poly.length; i++) {
        const [ax, ay] = poly[i], [bx, by] = poly[(i + 1) % poly.length];
        const ex = bx - ax, ey = by - ay, L2 = ex * ex + ey * ey || 1;
        let u = ((x - ax) * ex + (y - ay) * ey) / L2; u = Math.max(0, Math.min(1, u));
        const dx = x - (ax + ex * u), dy = y - (ay + ey * u), dd = Math.hypot(dx, dy);
        if (dd < best.d) best = { d: dd, dx, dy };
      }
      const inside = pip(poly, x, y), n = best.d || 1;
      return { d: inside ? best.d : -best.d, nx: (inside ? best.dx : -best.dx) / n, ny: (inside ? best.dy : -best.dy) / n };
    }

    const rigs = {};
    let R = null, style = box.dataset.dvShake;
    function rig(st) {
      if (rigs[st]) return rigs[st];
      rigs[st] = (async () => {
        const d = GEO[st], u = URLS[st];
        // three files per design: the emptied window, the frame on top, and every piece in one strip
        const [base, front, sheet] = await Promise.all([loadImg(u.base), loadImg(u.front), loadImg(u.pieces)]);
        const el = document.createElement('div'); el.className = 'dv-shake__rig'; el.setAttribute('aria-hidden', 'true');
        const cv = document.createElement('canvas'); base.alt = ''; front.alt = ''; el.append(base, cv, front);
        const xs = d.cavity.map((q) => q[0]), ys = d.cavity.map((q) => q[1]);
        const bb = [Math.min(...xs) - 30, Math.min(...ys) - 30, Math.max(...xs) + 30, Math.max(...ys) + 30];
        const mm = d.mmpx, poly = d.cavity.map(([x, y]) => [x / mm, y / mm]);
        const pieces = d.pieces.map((q, i) => {
          const hx = q.cx / mm, hy = q.cy / mm, rad = Math.min(q.w, q.h) / mm * 0.36, room = Math.max(0, wallDist(poly, hx, hy).d);
          const r = (k) => { const v = Math.sin((i + 1) * 12.9898 + k * 78.233) * 43758.5453; return v - Math.floor(v); };   // fixed per piece
          return { q, img: sheet, hx, hy, rad: Math.min(rad, room), w0: 2 * Math.PI * (0.95 + 0.4 * r(1)), z0: 0.12 + 0.06 * r(2), sx: 0.95 + 0.3 * r(3), hop: 0.55 + 0.45 * r(6),
                   x: 0, y: 0, vx: 0, vy: 0, a: 0, va: 0, spin: (r(4) < .5 ? -1 : 1) * (0.022 + 0.016 * r(5)) };
        });
        pieces.forEach((A, i) => { A.near = pieces.map((B, j) => (j === i ? 0 : Math.min(A.rad + B.rad, Math.hypot(A.hx - B.hx, A.hy - B.hy)))); });
        return { st, el, cv, ctx: cv.getContext('2d'), d, mm, bb, poly, pieces, th: 0, om: 0, pushes: [], t: 0, lastPush: 0, acc: 0, last: 0, running: false, sized: 0 };
      })();
      return rigs[st];
    }

    function step(R, dt) {
      let push = 0;
      R.pushes = R.pushes.filter((p) => R.t < p.t0 || R.t - p.t0 < BOOK.pushT);
      for (const p of R.pushes) { if (R.t < p.t0) continue; const u = (R.t - p.t0) / BOOK.pushT; push += p.dir * BOOK.push * BOOK.w * Math.PI / (2 * BOOK.pushT) * Math.sin(Math.PI * u); }
      const zb = BOOK.z + 0.32 * smooth((R.t - R.lastPush - 0.55) / 0.6);            // once the shake is over the hand steadies the binder
      const al = -BOOK.w * BOOK.w * R.th - 2 * zb * BOOK.w * R.om + push;
      const omPrev = R.om; R.om += al * dt; R.th += R.om * dt;
      const turned = omPrev !== 0 && Math.sign(omPrev) !== Math.sign(R.om);           // end of a swing
      const s = Math.sin(R.th), c = Math.cos(R.th), om2 = R.om * R.om, px = PIVOT[0] / R.mm, py = PIVOT[1] / R.mm;
      const calm = smooth((R.t - R.lastPush - 0.75) / 0.7);
      for (const P of R.pieces) {
        const rx = P.hx + P.x - px, ry = P.hy + P.y - py;
        const fx = G * s - (-om2 * rx - al * ry), fy = G * (c - 1) - (-om2 * ry + al * rx);
        const w = P.w0 * (1 + 1.6 * calm), k = w * w, cd = 2 * (P.z0 + 0.6 * calm) * w;
        P.vx += (P.sx * (1 - calm) * fx - k * P.x - cd * P.vx) * dt;
        if (turned && calm < .5) P.vy -= P.hop * Math.min(Math.abs(R.th), 0.12) * 2600 * (1 - calm);   // thrown up at the end of each swing
        const fall = P.y < 0 ? HOP_G * (1 - calm) : 0;
        P.vy += (fall + 0.5 * P.sx * (1 - calm) * fy - k * P.y - cd * P.vy) * dt;
        P.va += (P.spin * (1 - calm) * fx - (60 + 140 * calm) * P.a - (5 + 14 * calm) * P.va) * dt;
        P.x += P.vx * dt; P.y += P.vy * dt; P.a += P.va * dt;
        if (P.y > 0 && calm < 1) { P.y *= 0.5; if (P.vy > 0) P.vy *= -0.35; }               // lands on the stack under it
        const W = wallDist(R.poly, P.hx + P.x, P.hy + P.y);
        if (W.d < P.rad) {
          const pen = P.rad - W.d; P.x += W.nx * pen; P.y += W.ny * pen;
          const vn = P.vx * W.nx + P.vy * W.ny;
          if (vn < 0) { P.vx -= 1.55 * vn * W.nx; P.vy -= 1.55 * vn * W.ny; P.va += 0.012 * vn * (P.spin > 0 ? 1 : -1); }
        }
      }
      const ps = R.pieces;
      for (let i = 0; i < ps.length; i++) for (let j = i + 1; j < ps.length; j++) {
        const A = ps[i], B = ps[j], dx = (B.hx + B.x) - (A.hx + A.x), dy = (B.hy + B.y) - (A.hy + A.y), d = Math.hypot(dx, dy) || 1e-6, m = A.near[j];
        if (d >= m) continue;
        const nx = dx / d, ny = dy / d, pen = (m - d) / 2;
        A.x -= nx * pen; A.y -= ny * pen; B.x += nx * pen; B.y += ny * pen;
        const vr = (B.vx - A.vx) * nx + (B.vy - A.vy) * ny;
        if (vr < 0) { const imp = 0.75 * vr; A.vx += imp * nx; A.vy += imp * ny; B.vx -= imp * nx; B.vy -= imp * ny; A.va -= 0.006 * vr; B.va += 0.006 * vr; }
      }
      R.t += dt;
    }
    function size(R) {
      const Wd = box.clientWidth, dpr = Math.min(2, window.devicePixelRatio || 1), k = Wd / R.d.size;
      if (R.sized === Wd * dpr) return; R.sized = Wd * dpr;
      const [x0, y0, x1, y1] = R.bb;
      Object.assign(R.cv.style, { left: x0 * k + 'px', top: y0 * k + 'px', width: (x1 - x0) * k + 'px', height: (y1 - y0) * k + 'px' });
      R.cv.width = Math.round((x1 - x0) * k * dpr); R.cv.height = Math.round((y1 - y0) * k * dpr); R.scale = k * dpr;
      R.path = new Path2D(); R.d.cavity.forEach(([x, y], i) => (i ? R.path.lineTo(x - x0, y - y0) : R.path.moveTo(x - x0, y - y0))); R.path.closePath();
    }
    function draw(R) {
      const ctx = R.ctx, [x0, y0] = R.bb;
      ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.clearRect(0, 0, R.cv.width, R.cv.height);
      ctx.setTransform(R.scale, 0, 0, R.scale, 0, 0); ctx.save(); ctx.clip(R.path);
      for (const P of R.pieces) {
        const q = P.q; ctx.save(); ctx.translate(q.cx - x0 + P.x * R.mm, q.cy - y0 + P.y * R.mm); ctx.rotate(P.a);
        ctx.drawImage(P.img, q.sx, 0, q.w, q.h, q.x - q.cx, q.y - q.cy, q.w, q.h); ctx.restore();
      }
      ctx.restore(); R.el.style.transform = `rotate(${R.th}rad)`;
    }
    const quiet = (R) => Math.abs(R.th) < 2e-4 && Math.abs(R.om) < 2e-3 && !R.pushes.length &&
      R.pieces.every((P) => Math.abs(P.x) < .03 && Math.abs(P.y) < .03 && Math.abs(P.vx) < .4 && Math.abs(P.vy) < .4 && Math.abs(P.a) < .002);
    function loop(R, now) {
      if (R !== cur()) return;
      const dt = (now - R.last) / 1000; R.last = now;
      R.acc += Math.min(dt, 0.05);
      while (R.acc >= DT) { step(R, DT); R.acc -= DT; }
      size(R); draw(R);
      if (quiet(R) && R.t > 0.4) {
        R.th = R.om = 0; R.pieces.forEach((P) => { P.x = P.y = P.vx = P.vy = P.a = P.va = 0; }); draw(R);
        R.running = false; box.classList.remove('is-live'); return;
      }
      requestAnimationFrame((t) => loop(R, t));
    }
    let active = null; const cur = () => active;
    let dir = 1;
    // tapped before the layers have arrived: the binder (the photo itself) rocks right away, so a tap never feels dead
    function quickRock() {
      still.classList.remove('is-rocking'); void still.offsetWidth; still.classList.add('is-rocking');
    }
    async function shake() {
      if (reduce) return;
      let ready = false; const pr = rig(style); pr.then(() => { ready = true; }, () => {});
      await Promise.resolve(); await Promise.resolve();
      if (!ready) { quickRock(); return; }
      let r; try { r = await pr; } catch (e) { return; }
      if (r.st !== style) return;
      if (!r.el.isConnected) { box.querySelectorAll('.dv-shake__rig').forEach((n) => n.remove()); box.insertBefore(r.el, box.querySelector('.dv-shake__tap')); r.sized = 0; }
      active = r;
      if (!r.running) { r.t = 0; r.pushes = []; }
      r.pushes.push({ t0: r.t, dir }, { t0: r.t + BOOK.back, dir: -dir }); r.lastPush = r.t + BOOK.back; dir = -dir;
      box.classList.add('was-shaken');
      if (!r.running) {
        r.running = true; r.acc = 0; size(r); draw(r); box.classList.add('is-live');
        requestAnimationFrame((t) => { r.last = t; requestAnimationFrame((t2) => loop(r, t2)); });
      }
    }
    box.addEventListener('click', shake);
    box.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); shake(); } });
    wrap.querySelectorAll('[data-dv-shake-pick]').forEach((b) => b.addEventListener('click', () => {
      const st = b.dataset.dvShakePick; if (st === style || !URLS[st]) return;
      wrap.querySelectorAll('[data-dv-shake-pick]').forEach((x) => x.setAttribute('aria-pressed', String(x === b)));
      if (active) { active.running = false; active = null; }
      box.classList.remove('is-live', 'was-shaken'); box.querySelectorAll('.dv-shake__rig').forEach((n) => n.remove());
      style = st; box.dataset.dvShake = st; still.removeAttribute('data-dv-retry'); still.srcset = URLS[st].photoSet; still.src = URLS[st].photo;
      rig(st).then(() => setTimeout(shake, 120)).catch(() => {});     // switching designs gives the new one a shake straight away
    }));
    addEventListener('resize', () => Object.values(rigs).forEach((p) => p.then((r) => { r.sized = 0; })));
    near(box, () => rig(style).catch(() => {}));                    // fetch the layers ahead of the first tap
  })();

  /* ---------- 2 · how it works ----------
     The clip follows the scroll with a little easing (scroll back to rewind); doodles live in the binder's own millimetres (y up). */
  (function () {
    const sec = document.querySelector('[data-dv-how]');
    if (!sec) return;
    const vid = sec.querySelector('[data-dv-how-vid]'), ink = sec.querySelector('[data-dv-how-ink]');
    const steps = [...sec.querySelectorAll('[data-dv-how-steps] li')], mstep = sec.querySelector('[data-dv-how-m]');
    const W = sec.dataset.ink.split('|'), SUB = sec.dataset.subs.split('|');
    const FPS = 30, NF = 266;
    const mob = () => matchMedia('(max-width: 820px)').matches;
    let kind = '', armed = false;
    // worst case: the phone won't load the clip at all (low power mode, an in-app browser that blocks video, a dead connection).
    // Then the desk shows one still per step that cross-fade as you scroll, so the story still reads. ?dvhow=frames forces it.
    const framesBox = sec.querySelector('[data-dv-how-frames]');
    const KF = [10, 44, 58, 132, 152, 200, 266];
    const forced = /[?&]dvhow=frames/.test(location.search);
    let fb = '', fbKind = '', frameImgs = [], watchdog = 0;
    function useFrames(why) {
      if (!fb) { fb = why; sec.classList.add('is-frames'); }
      if (fbKind !== kind) {
        fbKind = kind; framesBox.textContent = '';
        frameImgs = (sec.dataset[kind + 'Frames'] || '').split('|').filter(Boolean).map((u) => { const im = new Image(); im.alt = ''; im.decoding = 'async'; im.src = u; framesBox.appendChild(im); return im; });
      }
      shownFrame = -1; synced = false; readScroll();
    }
    function useVideo() { if (!fb || forced) return; fb = ''; sec.classList.remove('is-frames'); synced = false; readScroll(); }
    function pickSource() {
      const k = mob() ? 'mob' : 'desk'; if (k === kind) return; kind = k;
      vid.poster = sec.dataset[k + 'Poster'];
      if (fb) useFrames(fb);
      if (armed) { vid.src = sec.dataset[k]; vid.load(); prime(); }
    }
    // iPhone Safari only starts buffering a video once it has played: give it a silent play/pause as it comes near.
    // If the browser refuses outright, there is nothing to wait for: go straight to the stills.
    const prime = () => {
      const p = vid.play();
      if (p && p.then) p.then(() => { vid.pause(); synced = false; readScroll(); }).catch((e) => { if (e && e.name === 'NotAllowedError') useFrames('blocked'); });
    };
    near(sec, () => {
      armed = true;
      if (forced) { useFrames('forced'); return; }
      vid.preload = 'auto'; vid.src = sec.dataset[kind]; vid.load(); prime();
      clearTimeout(watchdog); watchdog = setTimeout(() => { if (vid.readyState < 2) useFrames('slow'); }, 6000);
    });
    vid.addEventListener('error', () => { if (armed) useFrames('error'); });
    vid.addEventListener('loadeddata', () => { if (fb === 'slow') useVideo(); });
    addEventListener('touchstart', () => { if (armed && vid.readyState < 3) prime(); }, { passive: true, once: true });
    const io = (u) => { u = Math.max(0, Math.min(1, u)); return u < .5 ? 4 * u * u * u : 1 - Math.pow(-2 * u + 2, 3) / 2; };
    // camera: centre x mm, frame width mm. Desktop is a fixed shot; on the phone the camera follows the action.
    // the same cameras as the render: desktop 16:9 fixed (480 mm across), phone 9:16 following the action with the binder above centre
    const K = [[1, 230, 224], [10, 230, 224], [44, 205, 407], [54, 190, 271], [60, 190, 271], [70, 222, 215], [232, 222, 215], [258, 230, 224], [266, 230, 224]];
    const tall = (x, w) => { const h = w * 16 / 9; return { x, y: 98 - .08 * h, w, h }; };
    function cam(f) {
      // desktop: binder right of centre while the checklist is up, then the camera glides so the closed binder ends dead centre
      if (!mob()) { const u = io((f - 236) / 26); return { x: 145 + (228.3 - 145) * u, y: 98 - .5 * u, w: 480, h: 270 }; }
      for (let i = 0; i < K.length - 1; i++) {
        const [fa, xa, wa] = K[i], [fb, xb, wb] = K[i + 1];
        if (f >= fa && f <= fb) { const u = io((f - fa) / Math.max(1, fb - fa)); return tall(xa + (xb - xa) * u, wa + (wb - wa) * u); }
      }
      return tall(230, 224);
    }
    // doodles: [first frame, last frame, kind, data]; one word per sheet from when it starts to fall until the next one does
    const D = [
      [2, 28, 'arrow', { p: [[322, 176], [292, 152]], bend: -10, text: W[0], at: [304, 186] }],
      [42, 60, 'press', { text: W[1] }],
      [52, 68, 'pop', { text: W[2], at: [152, 206], dy: 10 }],
      [60, 72, 'word', { text: W[3] }], [72, 83, 'word', { text: W[4] }], [83, 93, 'word', { text: W[5] }],
      [93, 102, 'word', { text: W[6] }], [102, 110, 'word', { text: W[7] }], [110, 117, 'word', { text: W[8] }],
      [117, 123, 'word', { text: W[9] }], [123, 128, 'word', { text: W[10] }], [128, 133, 'word', { text: W[11] }],
      [133, 146, 'word', { text: W[12] }],
      [140, 158, 'pinch', { text: W[13], at: [200, 97], anchor: 'start' }],
      [156, 216, 'arrow', { p: [[262, 22], [128, 22]], bend: 26, text: W[14], at: [176, 6] }],
      [244, 266, 'arrow', { p: [[316, 176], [292, 152]], bend: -8, text: W[15], at: [304, 186] }],
    ];
    const NS = 'http://www.w3.org/2000/svg';
    const el = (t, a) => { const e = document.createElementNS(NS, t); for (const k in a) e.setAttribute(k, a[k]); return e; };
    const P = ([x, y]) => [x, -y];
    function arrowPath(p0, p1, bend) {
      const [x0, y0] = P(p0), [x1, y1] = P(p1); const mx = (x0 + x1) / 2, my = (y0 + y1) / 2, dx = x1 - x0, dy = y1 - y0, L = Math.hypot(dx, dy) || 1;
      const cx = mx - dy / L * bend, cy = my + dx / L * bend, a = Math.atan2(y1 - cy, x1 - cx), h = 5;
      return `M${x0} ${y0} Q${cx} ${cy} ${x1} ${y1} M${x1 - h * Math.cos(a - .5)} ${y1 - h * Math.sin(a - .5)} L${x1} ${y1} L${x1 - h * Math.cos(a + .5)} ${y1 - h * Math.sin(a + .5)}`;
    }
    const groups = D.map(([a, b, kd, d]) => {
      const g = el('g', { opacity: 0 }), paths = [], texts = [];
      const addPath = (dd) => { const p = el('path', { d: dd, class: 'ink' }); g.appendChild(p); paths.push(p); };
      const addText = (t, [x, y], s0, anchor) => { const tx = el('text', { x, y: -y, 'font-size': s0, 'text-anchor': anchor || 'middle' }); tx.dataset.s0 = s0; tx.textContent = t || ''; g.appendChild(tx); texts.push(tx); };
      if (kd === 'arrow') { addPath(arrowPath(d.p[0], d.p[1], d.bend)); addText(d.text, d.at, 9); }
      if (kd === 'press') { addPath(arrowPath([186, 218], [170, 199], -6)); addPath(arrowPath([186, -22], [170, -3], 6)); addText(d.text, [196, 222], 8.5, 'start'); }
      if (kd === 'pop') { const o = d.dy || 0; addPath(`M146 ${-186 - o} l-3 -6 M152 ${-188 - o} l0 -7 M158 ${-186 - o} l3 -6`); addText(d.text, d.at, 11); }
      if (kd === 'word') { addText(d.text, [262, 196], 11); addPath('M242 -190 q20 3 40 -1'); }
      if (kd === 'pinch') { addPath(arrowPath([138, 100], [154, 100], 0)); addPath(arrowPath([197, 100], [181, 100], 0)); addText(d.text, d.at, 9, d.anchor); }
      ink.appendChild(g); return { a, b, g, paths, texts };
    });
    let laidOut = false, textMob = null;
    function layout() {
      groups.forEach((G) => G.paths.forEach((p) => { const L = p.getTotalLength(); p.style.strokeDasharray = L; p.dataset.len = L; }));
      laidOut = true;
    }
    function drawInk(f, camF) {
      const c = cam(camF || f), m = mob();
      ink.setAttribute('viewBox', `${c.x - c.w / 2} ${-(c.y + c.h / 2)} ${c.w} ${c.h}`);
      if (!laidOut) layout();
      if (textMob !== m) { textMob = m; groups.forEach((G) => G.texts.forEach((t) => t.setAttribute('font-size', t.dataset.s0 * (m ? 1.25 : 1.5)))); }
      groups.forEach((G) => {
        const u = (f - G.a) / (G.b - G.a);
        if (u < 0 || u > 1) { G.g.setAttribute('opacity', 0); return; }
        const dr = Math.min(1, u / .35), fade = u > .8 ? 1 - (u - .8) / .2 : 1;
        G.g.setAttribute('opacity', fade.toFixed(3));
        G.paths.forEach((p) => { p.style.strokeDashoffset = (p.dataset.len * (1 - dr)).toFixed(2); });
        G.texts.forEach((t) => t.setAttribute('opacity', Math.min(1, u / .25).toFixed(3)));
      });
    }
    const S = [[0, 12], [12, 46], [46, 60], [60, 142], [142, 156], [156, 234], [234, 267]];
    let shown = -1;
    const todo = sec.querySelector('.dv-how__todo');
    function stepUI(f) {
      todo.classList.toggle('is-away', !mob() && f > 238);
      const k = S.findIndex(([a, b]) => f >= a && f < b);
      steps.forEach((li, j) => { li.classList.toggle('is-on', j === k); li.classList.toggle('is-done', j < k); });
      if (k !== shown && k >= 0) {
        shown = k; mstep.textContent = `${k + 1}/7 · ${steps[k].querySelector('span').textContent}`;
        const sm = document.createElement('small'); sm.textContent = SUB[k] || ''; mstep.appendChild(sm);
      }
    }
    let target = 0, head = 0, running = false, synced = false, shownFrame = -1;
    function readScroll() {
      const r = sec.getBoundingClientRect(), span = sec.offsetHeight - innerHeight;
      target = Math.min(1, Math.max(0, -r.top / span));
      document.body.classList.toggle('dv-how-on', r.top < innerHeight * .5 && r.bottom > innerHeight * .5);
      if (!running) { running = true; requestAnimationFrame(tick); }
    }
    function tick() {
      head += (target - head) * 0.14;
      if (Math.abs(target - head) < 0.0004) head = target;
      const f = 1 + head * (NF - 1), t = (f - 1) / FPS;
      if (fb) {
        // stills: the one for the current step is shown; the doodles sit in that still's camera
        const k = Math.max(0, S.findIndex(([a, b]) => f >= a && f < b));
        if (k !== shownFrame) { frameImgs.forEach((im, i) => im.classList.toggle('is-on', i === k)); shownFrame = k; }
        synced = true; drawInk(f, KF[k]); stepUI(f);
        if (head !== target) requestAnimationFrame(tick); else running = false;
        return;
      }
      if (!vid.paused) vid.pause();
      synced = vid.readyState >= 1 && !vid.seeking && Math.abs(vid.currentTime - t) <= 0.5 / FPS;
      if (!synced && vid.readyState >= 1 && !vid.seeking) vid.currentTime = t;
      drawInk(f); stepUI(f);
      // keep going until the playhead has caught up and the video really shows that frame
      if (head !== target || !synced) requestAnimationFrame(tick); else running = false;
    }
    vid.addEventListener('seeked', () => { if (!running) { running = true; requestAnimationFrame(tick); } });
    // the clip is only ever scrubbed: if it starts playing by itself (the iPhone warm-up), stop it and put it back on the scroll position
    vid.addEventListener('playing', () => { vid.pause(); synced = false; readScroll(); });
    pickSource();
    addEventListener('resize', () => { pickSource(); readScroll(); });
    addEventListener('scroll', readScroll, { passive: true });
    vid.addEventListener('loadedmetadata', readScroll);
    readScroll();
  })();
})();
